import React, { useState } from 'react';
import { api } from '../utils/api';

const emptyForm = {
  propertyType: 'rental',
  address: '',
  estimatedValue: '',
  notes: '',
  purchasePrice: '',
  purchaseDate: '',
  downPayment: '',
  closingCosts: '',
  initialRehabCosts: '',
  loanAmount: '',
  interestRate: '',
  loanTermYears: '',
  loanStartDate: '',
  monthlyRent: '',
  annualPropertyTax: '',
  annualInsurance: '',
  monthlyHoa: '',
  propertyMgmtPct: '',
  maintenancePct: '5',
  capexPct: '5',
  vacancyPct: '5',
  monthlyUtilities: '',
  monthlyOtherExpenses: '',
};

export default function PropertyForm({ property, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    if (!property) return emptyForm;
    const f = { ...emptyForm };
    Object.keys(emptyForm).forEach((k) => {
      if (property[k] !== null && property[k] !== undefined) f[k] = String(property[k]);
    });
    return f;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    if (!form.address || !form.estimatedValue) {
      setError('Address and estimated value are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {};
      Object.keys(emptyForm).forEach((k) => {
        if (form[k] === '') { payload[k] = null; return; }
        if (['address', 'notes', 'propertyType', 'purchaseDate', 'loanStartDate'].includes(k)) {
          payload[k] = form[k];
        } else {
          payload[k] = parseFloat(form[k]);
        }
      });
      if (property) {
        await api.updateProperty(property.id, payload);
      } else {
        await api.addProperty(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const isRental = form.propertyType === 'rental';

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{property ? 'Edit Property' : 'Add Property'}</h3>
          <button style={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={styles.formBody}>
          <div style={styles.typeToggle}>
            <button
              style={isRental ? styles.typeBtnActive : styles.typeBtn}
              onClick={() => setForm((f) => ({ ...f, propertyType: 'rental' }))}
            >
              🏠 Rental
            </button>
            <button
              style={!isRental ? styles.typeBtnActive : styles.typeBtn}
              onClick={() => setForm((f) => ({ ...f, propertyType: 'homestead' }))}
            >
              🏡 Homestead (primary residence)
            </button>
          </div>

          <FieldGroup title="Basics">
            <Field label="Address" value={form.address} onChange={set('address')} placeholder="123 Main St, City, ST" />
            <Field label="Current estimated value" value={form.estimatedValue} onChange={set('estimatedValue')} type="number" prefix="$" />
            <Field label="Notes" value={form.notes} onChange={set('notes')} textarea />
          </FieldGroup>

          <FieldGroup title="Purchase & Cash Invested" hint="Used for cash-on-cash return and total ROI">
            <Field label="Purchase price" value={form.purchasePrice} onChange={set('purchasePrice')} type="number" prefix="$" />
            <Field label="Purchase date" value={form.purchaseDate} onChange={set('purchaseDate')} type="date" />
            <Field label="Down payment" value={form.downPayment} onChange={set('downPayment')} type="number" prefix="$" />
            <Field label="Closing costs" value={form.closingCosts} onChange={set('closingCosts')} type="number" prefix="$" />
            <Field label="Initial rehab/repair costs" value={form.initialRehabCosts} onChange={set('initialRehabCosts')} type="number" prefix="$" />
          </FieldGroup>

          <FieldGroup title="Mortgage" hint="Leave blank if paid off or purchased in cash">
            <Field label="Loan amount" value={form.loanAmount} onChange={set('loanAmount')} type="number" prefix="$" />
            <Field label="Interest rate" value={form.interestRate} onChange={set('interestRate')} type="number" suffix="%" step="0.001" />
            <Field label="Loan term" value={form.loanTermYears} onChange={set('loanTermYears')} type="number" suffix="years" />
            <Field label="Loan start date" value={form.loanStartDate} onChange={set('loanStartDate')} type="date" />
          </FieldGroup>

          {isRental && (
            <FieldGroup title="Rental Income">
              <Field label="Monthly rent" value={form.monthlyRent} onChange={set('monthlyRent')} type="number" prefix="$" />
            </FieldGroup>
          )}

          <FieldGroup title="Expenses">
            <Field label="Annual property tax" value={form.annualPropertyTax} onChange={set('annualPropertyTax')} type="number" prefix="$" />
            <Field label="Annual insurance" value={form.annualInsurance} onChange={set('annualInsurance')} type="number" prefix="$" />
            <Field label="Monthly HOA" value={form.monthlyHoa} onChange={set('monthlyHoa')} type="number" prefix="$" />
            {isRental && (
              <>
                <Field label="Property management" value={form.propertyMgmtPct} onChange={set('propertyMgmtPct')} type="number" suffix="% of rent" />
                <Field label="Maintenance reserve" value={form.maintenancePct} onChange={set('maintenancePct')} type="number" suffix="% of rent" />
                <Field label="CapEx reserve" value={form.capexPct} onChange={set('capexPct')} type="number" suffix="% of rent" />
                <Field label="Vacancy reserve" value={form.vacancyPct} onChange={set('vacancyPct')} type="number" suffix="% of rent" />
              </>
            )}
            <Field label="Monthly utilities (if landlord-paid)" value={form.monthlyUtilities} onChange={set('monthlyUtilities')} type="number" prefix="$" />
            <Field label="Other monthly expenses" value={form.monthlyOtherExpenses} onChange={set('monthlyOtherExpenses')} type="number" prefix="$" />
          </FieldGroup>

          {error && <p style={styles.error}>{error}</p>}
        </div>

        <div style={styles.modalFooter}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={styles.saveBtn} onClick={save} disabled={saving}>
            {saving ? 'Saving...' : property ? 'Save Changes' : 'Add Property'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldGroup({ title, hint, children }) {
  return (
    <div style={styles.fieldGroup}>
      <div style={styles.fieldGroupTitle}>{title}</div>
      {hint && <div style={styles.fieldGroupHint}>{hint}</div>}
      <div style={styles.fieldGrid}>{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', prefix, suffix, textarea, placeholder, step }) {
  return (
    <label style={textarea ? styles.fieldWideWrap : styles.fieldWrap}>
      <span style={styles.fieldLabel}>{label}</span>
      <div style={styles.fieldInputRow}>
        {prefix && <span style={styles.fieldAffix}>{prefix}</span>}
        {textarea ? (
          <textarea style={styles.textarea} value={value} onChange={onChange} placeholder={placeholder} rows={2} />
        ) : (
          <input style={styles.input} type={type} value={value} onChange={onChange} placeholder={placeholder} step={step || 'any'} />
        )}
        {suffix && <span style={styles.fieldAffix}>{suffix}</span>}
      </div>
    </label>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
  },
  modal: {
    background: '#111827', border: '1px solid #1e2a3a', borderRadius: '16px',
    maxWidth: '560px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '1.25rem 1.5rem', borderBottom: '1px solid #1e2a3a',
  },
  modalTitle: { color: '#fff', fontSize: '1.1rem', fontWeight: '700', fontFamily: "'Syne', sans-serif", margin: 0 },
  closeBtn: { background: 'transparent', border: 'none', color: '#6b7280', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 },
  formBody: { padding: '1.25rem 1.5rem', overflowY: 'auto' },
  typeToggle: { display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' },
  typeBtn: {
    flex: 1, background: 'transparent', border: '1px solid #1e2a3a', borderRadius: '8px',
    padding: '10px', color: '#6b7280', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', fontFamily: "'Syne', sans-serif",
  },
  typeBtnActive: {
    flex: 1, background: 'rgba(0,212,170,0.12)', border: '1px solid #00d4aa', borderRadius: '8px',
    padding: '10px', color: '#00d4aa', fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Syne', sans-serif",
  },
  fieldGroup: { marginBottom: '1.25rem' },
  fieldGroupTitle: { color: '#e5e7eb', fontSize: '0.85rem', fontWeight: '700', fontFamily: "'Syne', sans-serif", marginBottom: '2px' },
  fieldGroupHint: { color: '#6b7280', fontSize: '0.72rem', marginBottom: '0.6rem' },
  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' },
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldWideWrap: { display: 'flex', flexDirection: 'column', gap: '4px', gridColumn: '1 / -1' },
  fieldLabel: { color: '#9ca3af', fontSize: '0.72rem', fontWeight: '600' },
  fieldInputRow: { display: 'flex', alignItems: 'center', background: '#0a0f1e', border: '1px solid #1e2a3a', borderRadius: '6px', padding: '0 8px' },
  fieldAffix: { color: '#6b7280', fontSize: '0.75rem', flexShrink: 0 },
  input: { flex: 1, background: 'transparent', border: 'none', color: '#fff', padding: '8px 6px', fontSize: '0.8rem', fontFamily: "'DM Mono', monospace", outline: 'none', minWidth: 0 },
  textarea: { flex: 1, background: 'transparent', border: 'none', color: '#fff', padding: '8px 6px', fontSize: '0.8rem', fontFamily: "'Syne', sans-serif", outline: 'none', resize: 'vertical' },
  error: { color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' },
  modalFooter: {
    display: 'flex', justifyContent: 'flex-end', gap: '0.6rem',
    padding: '1rem 1.5rem', borderTop: '1px solid #1e2a3a',
  },
  cancelBtn: {
    background: 'transparent', border: '1px solid #374151', color: '#9ca3af', borderRadius: '8px',
    padding: '8px 18px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', fontFamily: "'Syne', sans-serif",
  },
  saveBtn: {
    background: '#00d4aa', border: 'none', color: '#0a0f1e', borderRadius: '8px',
    padding: '8px 18px', fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Syne', sans-serif",
  },
};
